import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import api from "../../globals/axios";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import GenericButton from "../../components/Custom/GenericButton";
import { useAlert } from "../../components/Alert";
import CustomCard from "../../components/Famer/GenericCard";
import LoaderContainer from "../../components/LoaderContainer";

/**
 * type: "program" | "stage"
 * If type is "program", expects programId param and shows stages.
 * If type is "stage", expects programId and stageId params and shows activities.
 */
const ProgramOrStageDetail = ({ type }) => {
  const { programId, stageId } = useParams();
  const [program, setProgram] = useState(null);
  const [stage, setStage] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const isProgram = type === "program";
  const items = isProgram ? program?.stages : stage?.activities;
  const hasItems = isProgram
    ? program?.stages?.length > 0
    : stage?.activities?.length > 0;

  useEffect(() => {
    if (programId && (isProgram || stageId)) {
      fetchDetails();
    }
  }, [isProgram, programId, stageId]);

  const fetchDetails = async () => {
    try {
      const { data } = await api(`/farm-activities/programs/${programId}`);
      if (isProgram) {
        setProgram(data.data);
      } else {
        const stage = data.data.stages.find(
          (s) => parseInt(s.id) === parseInt(stageId)
        );
        setStage(stage);
      }
    } catch (error) {
      console.error("Error fetching program details:", error);
      showAlert("error", t("Failed to fetch program details"));
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers
  const onCreateStage = () => {
    navigate(`/pop-manager/program/${programId}/create-stage`);
  };
  const onCreateActivity = () => {
    navigate(
      `/pop-manager/program/${programId}/stage/${stageId}/create-activity`
    );
  };
  const onViewActivities = (stageId) => {
    navigate(`/pop-manager/program/${programId}/stage/${stageId}`);
  };
  const onViewProgram = () => {
    navigate(`/pop-manager/program/${programId}`);
  };

  const renderCard = (item) => {
    const data = isProgram
      ? {
          [t("Stage Name")]: item.name,
          [t("Start Day")]: item.start_day || "N/A",
          [t("End Day")]: item.end_day || "N/A",
          [t("Activities")]: item.activities.length || 0,
        }
      : {
          [t("Activity Name")]: item.name,
          [t("Start Day")]: item.start_day || "N/A",
          [t("End Day")]: item.end_day || "N/A",
          [t("Description")]: item.description || 0,
          [t("Priority")]: item.priority || "N/A",
        };

    return (
      <div key={item.id} style={{ width: "100%" }}>
        <CustomCard
          key={item.id}
          id={item.id}
          t={t}
          data={data}
          handlePrimaryButton={
            isProgram ? () => onViewActivities(item.id) : undefined
          }
          primaryLabel={isProgram ? t("View Activities") : undefined}
        />
      </div>
    );
  };

  return (
    <LoaderContainer loading={loading}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          minHeight: "calc(100vh - 4rem)",
          justifyContent: "space-between",
          maxWidth: "400px",
          margin: "0 auto",
        }}
      >
        <CustomTitle>
          {isProgram
            ? program?.name || t("Program Details")
            : stage?.name || t("Stage Details")}
        </CustomTitle>
        {(isProgram ? program : stage) && (
          <>
            {/* List Section */}
            <div style={{ marginTop: "16px" }}>
              <h3
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#333",
                  fontWeight: 600,
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                }}
              >
                <ScheduleIcon />
                {isProgram
                  ? `${t("Stages")} (${program.stages.length})`
                  : `${t("Activities")} (${stage.activities.length})`}
              </h3>

              {!hasItems ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    padding: "32px 16px",
                    border: "2px dashed #ddd",
                    borderRadius: "8px",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <div
                    style={{
                      fontSize: "60px",
                      color: "#999",
                      marginBottom: "16px",
                    }}
                  >
                    <ScheduleIcon />
                  </div>
                  <h4
                    style={{
                      color: "#666",
                      margin: "0 0 8px 0",
                      fontSize: "18px",
                    }}
                  >
                    {type === "program"
                      ? t("No Stages Found")
                      : t("No Activities Found")}
                  </h4>
                  <p
                    style={{
                      color: "#999",
                      margin: "0",
                      fontSize: "14px",
                    }}
                  >
                    {type === "program"
                      ? t("Add stages to organize your program activities")
                      : t("Add activities to organize your program stages")}
                  </p>
                </div>
              ) : (
                <Box
                  sx={{
                    maxHeight: "calc(100vh - 20rem)",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {items.map(renderCard)}
                </Box>
              )}
            </div>
          </>
        )}
        <Box sx={{ mt: 2 }}>
          {!isProgram && (
            <GenericButton
              backgroundColor="primary"
              fontColor="#fff"
              icon={ViewIcon}
              padding="6px 0px"
              onClick={onViewProgram}
            >
              {t("View Program")}
            </GenericButton>
          )}

          <GenericButton
            backgroundColor="primary"
            fontColor="#fff"
            icon={AddIcon}
            padding="6px 0px"
            onClick={isProgram ? onCreateStage : onCreateActivity}
          >
            {isProgram ? t("Add Stage") : t("Add Activity")}
          </GenericButton>
        </Box>
      </div>
    </LoaderContainer>
  );
};

ProgramOrStageDetail.propTypes = {
  type: PropTypes.oneOf(["program", "stage"]).isRequired,
};
export default ProgramOrStageDetail;
